import React from "react";
import MUIButton from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import PropTypes from "prop-types";
import ReactTooltip from "react-tooltip";
import Select from "react-select";

export default function ManualDocumentBuilderModal(props) {
  const {
    isOpen,
    mode,
    documentOptions,
    selectedDocumentOptions,
    onChange,
    orderIds,
    includeAor,
    onToggleIncludeAor,
    activeOrderId,
    latestPreview,
    onClose,
    onPreview,
    onProcess,
  } = props;

  const isLatestMode = mode === "latest";

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6">
          {isLatestMode
            ? "Process Order Paperwork"
            : `Process Order Paperwork${activeOrderId ? ` for Order ${activeOrderId}` : ""}`}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {isLatestMode
            ? "Rebuild the most recent document for each type on every entered order, force email to run last, then merge."
            : "Select one or more documents, then process each and finish with a merge."}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="overline">
            {isLatestMode ? "Orders To Process" : "Available Documents"}
          </Typography>

          {isLatestMode ? (
            <Stack spacing={2}>
              <Typography variant="body2" color="textSecondary">
                {orderIds.length} order(s) queued. Preview uses the first
                order's first rebuilt document.
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeAor}
                    onChange={onToggleIncludeAor}
                  />
                }
                label="Include AoR document when present"
              />

              {orderIds.length > 0 ? (
                <Paper variant="outlined">
                  <List dense disablePadding>
                    {orderIds.map((orderId, index) => (
                      <React.Fragment key={orderId}>
                        <ListItem>
                          <ListItemText primary={`Order ${orderId}`} />
                        </ListItem>
                        {index < orderIds.length - 1 ? (
                          <Divider component="li" />
                        ) : null}
                      </React.Fragment>
                    ))}
                  </List>
                </Paper>
              ) : null}

              {latestPreview.length > 0 ? (
                <>
                  <Typography variant="overline">
                    Files That Will Be Rebuilt
                  </Typography>
                  <Paper variant="outlined">
                    <List dense disablePadding>
                      {latestPreview.map((preview, previewIndex) => (
                        <React.Fragment key={preview.orderId}>
                          <ListItem alignItems="flex-start">
                            {(() => {
                              const previewDocuments = includeAor
                                ? preview.documents
                                : preview.documents.filter(
                                    (document) =>
                                      document.documentType !==
                                        "AoR Document" &&
                                      document.documentType !== "AOR Document",
                                  );

                              return (
                                <ListItemText
                                  primary={`Order ${preview.orderId}`}
                                  secondary={
                                    preview.previewError
                                      ? `Preview unavailable: ${preview.previewError}`
                                      : previewDocuments.length > 0
                                        ? previewDocuments
                                            .map(
                                              (document) =>
                                                document.documentType,
                                            )
                                            .join(", ")
                                        : "No delivery documents found for the current toggle settings"
                                  }
                                />
                              );
                            })()}
                          </ListItem>
                          {previewIndex < latestPreview.length - 1 ? (
                            <Divider component="li" />
                          ) : null}
                        </React.Fragment>
                      ))}
                    </List>
                  </Paper>
                </>
              ) : null}
            </Stack>
          ) : (
            <Stack spacing={2}>
              <Select
                placeholder="Choose document(s) to process"
                options={documentOptions}
                value={selectedDocumentOptions}
                isMulti
                menuPlacement="auto"
                menuPosition="fixed"
                menuShouldScrollIntoView={false}
                maxMenuHeight={320}
                onChange={onChange}
              />

              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="textSecondary">
                  {selectedDocumentOptions.length} selected
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Preview uses the first selected document.
                </Typography>
              </Stack>

              {selectedDocumentOptions.length > 0 ? (
                <Paper variant="outlined">
                  <List dense disablePadding>
                    {selectedDocumentOptions.map(
                      (selectedDocumentOption, index) => (
                        <React.Fragment key={selectedDocumentOption.value}>
                          <ListItem>
                            <ListItemText
                              primary={selectedDocumentOption.label}
                            />
                          </ListItem>
                          {index < selectedDocumentOptions.length - 1 ? (
                            <Divider component="li" />
                          ) : null}
                        </React.Fragment>
                      ),
                    )}
                  </List>
                </Paper>
              ) : null}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <MUIButton
          variant="contained"
          color="success"
          data-tip={
            isLatestMode
              ? "Populate the text area with the first document that will be rebuilt"
              : "Populate the text area with the first selected document"
          }
          disabled={
            isLatestMode
              ? orderIds.length === 0
              : selectedDocumentOptions.length === 0
          }
          onClick={onPreview}
          size="small"
          startIcon={<CheckIcon />}
        >
          Preview
        </MUIButton>
        <MUIButton
          variant="contained"
          color="primary"
          data-tip={
            isLatestMode
              ? "Process the latest document set for each order, then merge"
              : "Process all selected documents, then merge"
          }
          disabled={
            isLatestMode
              ? orderIds.length === 0
              : selectedDocumentOptions.length === 0
          }
          onClick={onProcess}
          size="small"
        >
          {isLatestMode ? "Rebuild + Merge" : "Process + Merge"}
        </MUIButton>
        <MUIButton
          variant="contained"
          color="error"
          data-tip={"Cancel"}
          onClick={onClose}
          size="small"
          startIcon={<CloseIcon />}
        >
          Cancel
        </MUIButton>
        <ReactTooltip />
      </DialogActions>
    </Dialog>
  );
}

ManualDocumentBuilderModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  mode: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onPreview: PropTypes.func.isRequired,
  onProcess: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  orderIds: PropTypes.array.isRequired,
  includeAor: PropTypes.bool.isRequired,
  onToggleIncludeAor: PropTypes.func.isRequired,
  activeOrderId: PropTypes.string.isRequired,
  latestPreview: PropTypes.array.isRequired,
  documentOptions: PropTypes.array.isRequired,
  selectedDocumentOptions: PropTypes.array.isRequired,
};
